import React, { memo, useEffect, useRef } from "react";
import { Animated, Easing, StyleSheet, View } from "react-native";

import Colors from "../../Resources/Color";

const DOT_COUNT = 3;
const DEFAULT_DOT_SIZE = 5;
const DEFAULT_SPACING = 6;
const DEFAULT_DURATION_MS = 900;

function getDotAnimation(progress, index) {
  const start = index * 0.16;
  const peak = start + 0.12;
  const end = start + 0.24;

  return {
    opacity: progress.interpolate({
      inputRange: [0, start, peak, end, 1],
      outputRange: [0.42, 0.42, 1, 0.42, 0.42],
      extrapolate: "clamp",
    }),
    transform: [
      {
        translateY: progress.interpolate({
          inputRange: [0, start, peak, end, 1],
          outputRange: [0, 0, -4, 0, 0],
          extrapolate: "clamp",
        }),
      },
      {
        scale: progress.interpolate({
          inputRange: [0, start, peak, end, 1],
          outputRange: [1, 1, 1.06, 1, 1],
          extrapolate: "clamp",
        }),
      },
    ],
  };
}

function ThreeDotLoader({
  color = Colors.grayColor,
  dotSize = DEFAULT_DOT_SIZE,
  spacing = DEFAULT_SPACING,
  style = null,
}) {
  const progress = useRef(new Animated.Value(0)).current;
  const animationRef = useRef(null);
  const isUnmountedRef = useRef(false);

  useEffect(() => {
    isUnmountedRef.current = false;

    const runAnimation = () => {
      progress.setValue(0);
      animationRef.current = Animated.timing(progress, {
        toValue: 1,
        duration: DEFAULT_DURATION_MS,
        easing: Easing.linear,
        useNativeDriver: true,
      });
      animationRef.current.start(({ finished }) => {
        if (!finished || isUnmountedRef.current) {
          return;
        }

        runAnimation();
      });
    };

    runAnimation();

    return () => {
      isUnmountedRef.current = true;
      animationRef.current?.stop?.();
      progress.stopAnimation();
      progress.setValue(0);
    };
  }, [progress]);

  return (
    <View
      style={[styles.container, style]}
      accessibilityRole="progressbar"
      accessibilityLabel="Loading"
    >
      {Array.from({ length: DOT_COUNT }, (_, index) => (
        <Animated.View
          key={`dot-${index}`}
          style={[
            styles.dot,
            index < DOT_COUNT - 1 ? { marginRight: spacing } : null,
            {
              width: dotSize,
              height: dotSize,
              borderRadius: dotSize / 2,
              backgroundColor: color,
            },
            getDotAnimation(progress, index),
          ]}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "center",
  },
  dot: {
    flexShrink: 0,
  },
});

export default memo(ThreeDotLoader);
